import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Search, Package, Truck, Home, CheckCircle2, Clock, MapPin } from "lucide-react";

const STEPS = [
  { key: "pending", label: "Order Received", icon: Package },
  { key: "picked_up", label: "Picked Up", icon: Truck },
  { key: "in_transit", label: "In Transit", icon: Truck },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Home },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

function stepIndex(status) {
  return STEPS.findIndex((s) => s.key === status);
}

export default function Track() {
  const [tn, setTn] = useState("");
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    if (!tn.trim()) return;
    setLoading(true);
    setError("");
    setShipment(null);
    try {
      const { data } = await api.get(`/track/${tn.trim().toUpperCase()}`);
      setShipment(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Tracking number not found.");
    } finally {
      setLoading(false);
    }
  };

  const idx = shipment ? stepIndex(shipment.current_status) : -1;

  return (
    <div data-testid="track-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">
          Shipment Tracking
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter">
          Where's my freight?
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Enter your tracking number below to see the latest status of your shipment.
          Try <span className="text-primary-brand font-bold">LPLDEMO1234</span> for a demo.
        </p>

        <form onSubmit={search} className="mt-10 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center bg-white border border-slate-200 focus-within:border-primary-brand">
            <Search className="h-5 w-5 text-slate-500 ml-4" />
            <input
              value={tn}
              onChange={(e) => setTn(e.target.value)}
              placeholder="Enter tracking number (e.g. LPL1234ABCD)"
              data-testid="track-input"
              className="flex-1 bg-transparent outline-none px-4 py-4 text-slate-900 placeholder:text-slate-400 uppercase tracking-wider text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="track-submit-btn"
            className="bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
          >
            {loading ? "Searching…" : "Track"}
          </button>
        </form>

        {error && (
          <div data-testid="track-error" className="mt-6 border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {shipment && (
            <motion.div
              key={shipment.tracking_number}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="track-result"
              className="mt-12"
            >
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                <Info label="Tracking Number" value={shipment.tracking_number} />
                <Info label="From" value={shipment.origin} />
                <Info label="To" value={shipment.destination} />
                <Info label="Customer" value={shipment.customer_name} />
                <Info label="Service" value={shipment.service_type} />
                <Info label="Status" value={STEPS[idx]?.label || shipment.current_status} accent />
              </div>

              {/* Progress bar */}
              <div className="mt-14">
                <div className="hidden md:grid grid-cols-5 gap-4">
                  {STEPS.map((s, i) => {
                    const active = i <= idx;
                    const Icon = s.icon;
                    return (
                      <div key={s.key} className="flex flex-col items-start">
                        <div className={`h-12 w-12 flex items-center justify-center border ${active ? "bg-primary-brand border-primary-brand text-white" : "border-slate-200 text-slate-400"}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className={`mt-3 text-xs uppercase tracking-[0.2em] font-bold ${active ? "text-slate-900" : "text-slate-400"}`}>
                          {s.label}
                        </div>
                        <div className={`mt-3 h-px w-full ${active ? "bg-primary-brand" : "bg-zinc-800"}`} />
                      </div>
                    );
                  })}
                </div>

                {/* Mobile vertical steps */}
                <div className="md:hidden space-y-3">
                  {STEPS.map((s, i) => {
                    const active = i <= idx;
                    const Icon = s.icon;
                    return (
                      <div key={s.key} className={`flex items-center gap-3 px-4 py-3 border ${active ? "border-primary-brand bg-primary-brand/10" : "border-slate-200"}`}>
                        <Icon className={`h-5 w-5 ${active ? "text-primary-brand" : "text-slate-400"}`} />
                        <div className={`text-xs uppercase tracking-[0.2em] font-bold ${active ? "text-slate-900" : "text-slate-400"}`}>{s.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Event history */}
              <div className="mt-14">
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-primary-brand mb-4">Event History</div>
                <div className="border border-slate-200">
                  {(shipment.events || []).slice().reverse().map((ev) => (
                    <div key={`${ev.timestamp}-${ev.status}`} className="grid grid-cols-1 md:grid-cols-4 gap-4 px-6 py-5 border-b border-slate-200 last:border-b-0">
                      <div className="flex items-center gap-2 text-slate-900 font-heading font-bold uppercase text-sm tracking-wide">
                        <Clock className="h-4 w-4 text-primary-brand" />
                        {STEPS.find((s) => s.key === ev.status)?.label || ev.status}
                      </div>
                      <div className="text-slate-600 text-sm flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" /> {ev.location || "—"}
                      </div>
                      <div className="text-slate-500 text-sm md:col-span-1">{ev.note || ""}</div>
                      <div className="text-slate-400 text-xs md:text-right">
                        {new Date(ev.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div className="bg-brand-bg px-6 py-5">
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</div>
      <div className={`mt-2 font-heading font-bold ${accent ? "text-primary-brand" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
